/**
 * BudgetEnvelope — top-of-results slider that highlights which results
 * fit inside the user's total 3-year spend envelope. Doesn't re-rank;
 * decorates each ResultCard with a "fits budget ✓" or "over by $X" chip
 * so cost-anxious families can scan their shortlist without doing
 * mental arithmetic.
 *
 * Extracted from pages/AdvancedResults.jsx (2026-02-04) to reduce
 * that file's bloat.
 */
import { Slider } from "@/components/ui/slider";
import { trackDebounced, EVENTS } from "@/lib/track";

export default function BudgetEnvelope({
  budget,
  onChange,
  results,
  format,
  defaultValue,
  min = 30_000,
  max = 300_000,
  step = 5_000,
}) {
  const totals = (results || []).map(
    (r) => Number(r.financial?.net_degree_cost_usd ?? r.financial?.total_degree_usd ?? 0),
  );
  const fits = totals.filter((t) => t > 0 && t <= budget).length;
  const total = totals.filter((t) => t > 0).length;
  const pct = total ? Math.round((fits / total) * 100) : 0;
  const isDefault = budget === defaultValue;
  return (
    <div
      className="mt-4 rounded-lg border border-[#4C1D95]/25 bg-white overflow-hidden"
      data-testid="budget-envelope"
    >
      <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(260px,1fr)] gap-4 px-5 py-4 items-center">
        <div>
          <div className="flex items-center gap-2 label-uppercase text-[#4C1D95]">
            <span>Total 3-year budget</span>
          </div>
          <div className="mt-1 font-editorial text-2xl leading-tight text-stone-900">
            <span data-testid="budget-envelope-count">{fits}</span>{" "}
            <span className="text-stone-500">of {total}</span>{" "}
            <span className="text-stone-500 text-lg">fit your</span>{" "}
            <span className="text-[#4C1D95]" data-testid="budget-envelope-value">
              {format(budget)}
            </span>{" "}
            <span className="text-stone-500 text-lg">envelope</span>
          </div>
          <p className="text-[11px] text-stone-500 mt-1 leading-snug">
            Tuition + estimated 3-year living costs. Drag to see how many of
            your matches stay inside the family budget — nothing is hidden,
            we just tag each card.
          </p>
        </div>
        <div className="w-full">
          <div className="flex items-baseline justify-between text-[11px] text-stone-500 mb-2">
            <span>{format(min)}</span>
            <span className="font-mono text-[#4C1D95]">{pct}% fit</span>
            <span>{format(max)}</span>
          </div>
          <Slider
            data-testid="budget-envelope-slider"
            min={min}
            max={max}
            step={step}
            value={[budget]}
            onValueChange={(v) => {
              const next = Math.max(min, Math.min(max, v[0]));
              onChange(next);
              trackDebounced(EVENTS.BUDGET_SLIDER, {
                value: next,
                page: "/advanced",
                fits,
                total,
              });
            }}
            className="mt-1"
          />
          <div className="mt-2 flex items-center justify-end">
            <button
              type="button"
              data-testid="budget-envelope-reset"
              onClick={() => onChange(defaultValue)}
              disabled={isDefault}
              className="text-[10px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline disabled:text-stone-300 disabled:no-underline"
            >
              Reset to default
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
